import React from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { ProfitAndLossResponse } from "@common/types/reports/types";
import { ReportPresentationChrome } from "@common/schemas/domains/reportSettings/report-presentation.schema";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { Entity } from "@common/types/entity";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { AccountingReportFilters } from "../types";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { AccountingReportFooter } from "../AccountingReportFooter";
import {
    AccountingReportContext,
    useAccountingReportContextValue,
} from "../AccountingReport.context";
import { useReportChrome } from "../useReportChrome";
import { ProfitLossTable } from "./ProfitLossTable";
import { ProfitLossDrillDownParams } from "./profitLossDrillDown.lib";
import { ProfitLossViewRow } from "./profitLossViewTypes";
import styles from "./ProfitLossPreviewContent.module.scss";

interface Props {
    profitLoss: ProfitAndLossResponse;
    filters: AccountingReportFilters;
    chrome: ReportPresentationChrome;
    numberFormat: NumberFormatValues;
    generationDate: Date;
    entity: Entity;
    rows: ProfitLossViewRow[];
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    tableRef: React.RefObject<ViewTableHandle | null>;
    onDrillDown: (params: ProfitLossDrillDownParams) => void;
}

export const ProfitLossPreviewContent: React.FC<Props> = ({
    profitLoss,
    filters,
    chrome,
    numberFormat,
    generationDate,
    entity,
    rows,
    expanded,
    onExpandedChange,
    tableRef,
    onDrillDown,
}) => {
    const resolvedChrome = useReportChrome({
        chrome,
        generationDate,
    });
    const accountingReportContextValue = useAccountingReportContextValue(
        filters,
        numberFormat,
    );

    if (!entity) {
        return null;
    }

    return (
        <Card stacked sharpEdges fullPage>
            <AccountingReportContext.Provider
                value={accountingReportContextValue}
            >
                <CardSection className={styles.header}>
                    <AccountingReportHeader chrome={resolvedChrome} />
                </CardSection>
                <ProfitLossTable
                    report={profitLoss}
                    rows={rows}
                    expanded={expanded}
                    onExpandedChange={onExpandedChange}
                    tableRef={tableRef}
                    onDrillDown={onDrillDown}
                />
                <AccountingReportFooter chrome={resolvedChrome} />
            </AccountingReportContext.Provider>
        </Card>
    );
};
